import { MapPin } from 'lucide-react';
import { C } from '../constants';
import { formatNumber } from '../utils';
import Glass from './Glass';

const TILE = 92;
const GAP  = 8;

export default function DistrictMap({ counts, userVote, highlight, onSelectDistrict, stateData }) {
  const { parties, partyById, districts, localFont, accent } = stateData;

  const cols = Math.max(...districts.map(d => d.col)) + 1;
  const rows = Math.max(...districts.map(d => d.row)) + 1;
  const W    = cols * (TILE + GAP) + GAP;
  const H    = rows * (TILE + GAP) + GAP;

  return (
    <Glass style={{ padding: '1.4rem' }}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <MapPin size={14} color={accent} />
          <span style={{
            fontSize: '0.7rem', letterSpacing: '0.18em', textTransform: 'uppercase',
            color: C.textDim, fontWeight: 600,
          }}>
            {stateData.name}{' '}
            <span style={{ fontFamily: localFont, textTransform: 'none', letterSpacing: 0, color: accent }}>
              · {stateData.nameLocal}
            </span>
          </span>
        </div>
        <span style={{ fontSize: '0.66rem', color: C.textDimmer, fontStyle: 'italic' }}>
          stylised tiles · roughly geographic
        </span>
      </div>

      <div className="overflow-x-auto pb-2">
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: W, height: 'auto', display: 'block' }}>
          <defs>
            <filter id="tileGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {districts.map(d => {
            const dCounts = counts[d.en] || {};
            const total   = Object.values(dCounts).reduce((a, b) => a + b, 0);

            let leading = null, leadingCount = 0, tied = [];
            for (const [pid, c] of Object.entries(dCounts)) {
              if (c > leadingCount)               { leading = pid; leadingCount = c; tied = [pid]; }
              else if (c === leadingCount && c > 0) { tied.push(pid); }
            }
            const isTie = tied.length > 1;
            if (isTie) {
              leading = (userVote && tied.includes(userVote.party))
                ? userVote.party
                : [...tied].sort()[0];
            }

            const fill           = total === 0 ? 'rgba(255,255,255,0.05)' : (partyById[leading]?.color || C.cardBg);
            const isHighlight    = highlight === d.en;
            const isUserDistrict = userVote && userVote.district === d.en;

            const x = d.col * (TILE + GAP) + GAP;
            const y = d.row * (TILE + GAP) + GAP;

            const stroke      = isHighlight ? accent : (isUserDistrict ? C.pink : (total === 0 ? C.border : 'rgba(255,255,255,0.18)'));
            const strokeWidth = isHighlight ? 2.5    : (isUserDistrict ? 2       : 1);

            return (
              <g
                key={d.en}
                onClick={() => onSelectDistrict && onSelectDistrict(d.en)}
                style={{ cursor: onSelectDistrict ? 'pointer' : 'default' }}
              >
                <rect
                  x={x} y={y} width={TILE} height={TILE} rx={10}
                  fill={fill} stroke={stroke} strokeWidth={strokeWidth}
                  style={{
                    transition: 'fill .5s ease',
                    filter: total > 0 ? `drop-shadow(0 0 8px ${partyById[leading]?.color}55)` : 'none',
                  }}
                />

                {isUserDistrict && (
                  <circle cx={x + TILE - 9} cy={y + 9} r={4} fill={C.pink}
                          style={{ filter: `drop-shadow(0 0 4px ${C.pink})` }} />
                )}

                {isTie && (
                  <g>
                    <rect x={x + 4} y={y + TILE - 16} width={28} height={12} rx={3}
                          fill={C.ink} stroke="rgba(255,255,255,0.3)" strokeWidth={0.5} opacity={0.92} />
                    <text x={x + 18} y={y + TILE - 7} textAnchor="middle"
                          fontSize="8" fontWeight="700" fill={accent}
                          style={{ letterSpacing: '0.12em' }}>TIE</text>
                  </g>
                )}

                <text
                  x={x + TILE / 2} y={y + TILE / 2 - 6}
                  textAnchor="middle" fontSize="10" fontWeight="700"
                  fill={total === 0 ? C.textDim : C.white}
                  style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '0.01em' }}
                >
                  {d.en.length > 11 ? d.en.slice(0, 10) + '…' : d.en}
                </text>
                <text
                  x={x + TILE / 2} y={y + TILE / 2 + 7}
                  textAnchor="middle" fontSize="9"
                  fill={total === 0 ? C.textDimmer : 'rgba(255,255,255,0.85)'}
                  style={{ fontFamily: localFont }}
                >
                  {d.local.length > 10 ? d.local.slice(0, 9) + '…' : d.local}
                </text>

                {total === 0 ? (
                  <text
                    x={x + TILE / 2} y={y + TILE - 9}
                    textAnchor="middle" fontSize="10" fill={C.textDimmer}
                    style={{ fontFamily: "'Poppins', sans-serif", fontStyle: 'italic' }}
                  >0</text>
                ) : (
                  <text
                    x={x + TILE / 2} y={y + TILE - 9}
                    textAnchor="middle" fontSize="10" fontWeight="700" fill={C.white}
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {leading} · {formatNumber(leadingCount)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${C.border}` }}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span style={{
            fontSize: '0.62rem', letterSpacing: '0.2em', textTransform: 'uppercase',
            color: C.textDim, fontWeight: 600,
          }}>
            Legend
          </span>
          {parties.map(p => (
            <span key={p.id} className="inline-flex items-center gap-1.5">
              <span style={{ width: 10, height: 10, backgroundColor: p.color, borderRadius: 2, boxShadow: `0 0 6px ${p.color}AA`, display: 'inline-block' }} />
              <span style={{ fontSize: '0.72rem', color: C.textDim }}>{p.en}</span>
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 10, height: 10, backgroundColor: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 2, display: 'inline-block' }} />
            <span style={{ fontSize: '0.72rem', color: C.textDim }}>No votes</span>
          </span>
        </div>
      </div>
    </Glass>
  );
}
